import type { ActionFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import invariant from 'tiny-invariant';
import type {
    CreateOrUpdateInspirationMutation,
    CreateOrUpdateInspirationMutationVariables,
} from 'generated/graphql';
import { CreateOrUpdateInspirationDocument } from 'generated/graphql';

export const CREATE_BUILD_HUB_INSPIRATION_API_ROUTE =
    '/remix-api/app/build-hub-inspiration-create';

const DEFAULT_FAILED_MESSAGE = 'Failed to create BuildHub inspiration.';

/**
 * Action to create a new inspiration group and link it to a BuildHub.
 */
export const action: ActionFunction = async ({ request, context }) => {
    const data = await request.formData();

    invariant(data.get('id'), 'No BuildHub ID submitted for update.');

    const variables: CreateOrUpdateInspirationMutationVariables = {
        BuildHubID: data.get('id') as string,
    };

    const inspirationGroupId = data.get('inspirationGroupId');
    if (inspirationGroupId) {
        variables.InspirationGroupID = inspirationGroupId as string;
    }

    const title = data.get('title');
    if (title) {
        variables.Title = title;
    }

    const description = data.get('description');
    if (description !== null && description !== undefined) {
        variables.Description = description;
    }

    const isPrivate = data.get('isPrivate');
    if (isPrivate) {
        variables.Private = ['true', '1'].includes(isPrivate);
    }

    const parentId = data.get('parentId');
    if (parentId) {
        variables.ParentID = Number(parentId);
    }

    const sortOrder = data.get('sortOrder');
    if (sortOrder) {
        variables.SortOrder = Number(sortOrder);
    }

    try {
        const { data: result } = await context.client.mutate<
            CreateOrUpdateInspirationMutation,
            CreateOrUpdateInspirationMutationVariables
        >({
            mutation: CreateOrUpdateInspirationDocument,
            variables,
        });

        const validResult = result?.projectInspirationGroupUpsert;
        if (!validResult) {
            return json({
                buildHubUpsert: {
                    __typename: 'ServerError',
                    Message: DEFAULT_FAILED_MESSAGE,
                },
            });
        }

        return json(result);
    } catch (error) {
        return json({
            buildHubUpsert: {
                __typename: 'ServerError',
                Message:
                    error instanceof Error
                        ? error.message
                        : DEFAULT_FAILED_MESSAGE,
            },
        });
    }
};
